import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useUsersCurrent } from '../../api/hooks/users/use-users-current';
import type { PermissionScope } from '../../api/interfaces/permissions';
import { useAuthStore } from '../stores/auth.store';
import { getCurrentUserAuthErrorMessage, shouldMarkCurrentUserSessionExpired } from '../utils/auth-error';
import { invalidatePermissionSensitiveQueries } from '../../api/utils/invalidate-permission-sensitive-queries';

export interface AuthProviderProps {
  readonly children?: ReactNode;
}

export const AuthProvider: FunctionComponent<AuthProviderProps> = (props) => {
  const { children } = props;

  const { hydrated, user, sessionExpired, setUser, setAuthErrorMessage, setSessionExpired, logout } = useAuthStore();
  const usersCurrent = useUsersCurrent(hydrated);
  const queryClient = useQueryClient();
  const previousScope = useRef<string | null>(null);

  useEffect(() => {
    if (!hydrated || usersCurrent.status === 'pending') {
      return;
    }

    if (usersCurrent.status === 'success') {
      const userData = usersCurrent.data;

      const validScopes: PermissionScope[] = ['OWN', 'BRANCH', 'SYSTEM'];
      const permissions = userData.effectivePermissions?.map((permission) => ({
        name: permission.name,
        scopes: permission.scopes.filter((scope): scope is PermissionScope =>
          validScopes.includes(scope as PermissionScope),
        ),
      })) ?? [];

      setUser({
        id: userData.id ?? '',
        email: userData.email ?? '',
        name: userData.name,
        roleName: userData.roleName,
        organisationType: userData.organisation?.type ?? null,
        canAccessCaseAttachments: userData.canAccessCaseAttachments
          ?? (userData.organisation?.type?.toUpperCase() === 'AGROS'),
        canUseSupportAssistant: userData.canUseSupportAssistant === true,
        branches: userData.branches ?? [],
        branchSource: userData.branchSource,
        permissions,
        isImpersonating: userData.isImpersonating,
      });
      const scope = JSON.stringify([
        userData.id, userData.organisation?.type, userData.branchSource,
        (userData.branches ?? []).map((branch) => branch.id).sort(),
        permissions, userData.isImpersonating,
      ]);
      if (previousScope.current !== null && previousScope.current !== scope) {
        void invalidatePermissionSensitiveQueries(queryClient, false);
      }
      previousScope.current = scope;
      setAuthErrorMessage(null);
      setSessionExpired(false);
      return;
    }

    if (usersCurrent.status === 'error') {
      const shouldShowSessionExpired = shouldMarkCurrentUserSessionExpired(
        usersCurrent.error,
        user !== null,
        sessionExpired,
      );
      logout();
      setAuthErrorMessage(getCurrentUserAuthErrorMessage(usersCurrent.error));
      setSessionExpired(shouldShowSessionExpired);
    }
  }, [
    hydrated,
    user,
    sessionExpired,
    usersCurrent.status,
    usersCurrent.data,
    usersCurrent.error,
    setUser,
    setAuthErrorMessage,
    setSessionExpired,
    logout,
    queryClient,
  ]);

  return <>{children}</>;
};
